import { call, put, select, takeLatest } from 'redux-saga/effects'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import {
  fetchListRequest,
  fetchListSuccess,
  fetchListFailure,
  deleteRequest,
  deleteSuccess,
  deleteFailure,
  restoreRequest,
  restoreSuccess,
  restoreFailure,
  setIsSavingResources,
} from './additionalProductsToolsSlice'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

export const additionalProductsToolsActions = {
  FETCH_LIST: 'ADDITIONAL_PRODUCTS_TOOLS/FETCH_LIST',
  DELETE: 'ADDITIONAL_PRODUCTS_TOOLS/DELETE',
  RESTORE: 'ADDITIONAL_PRODUCTS_TOOLS/RESTORE',
  UPDATE_RESOURCES: 'ADDITIONAL_PRODUCTS_TOOLS/UPDATE_RESOURCES',
}

function* fetchListSaga({ enqueue }) {
  try {
    yield put(fetchListRequest())
    const { extraRoutes } = yield select(selectAuthData)
    const listUrl = extraRoutes?.devTools?.additionalSalesProductsList
    const result = yield call(callApi, { endpoint: listUrl, showErrorSnackbar: false })
    yield put(fetchListSuccess(result))
  } catch (e) {
    yield put(fetchListFailure('Error fetching list'))
    if (enqueue) {
      yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message: 'Error fetching list' })
    }
  }
}

function* deleteSaga({ id, name, enqueue }) {
  try {
    yield put(deleteRequest(id))
    const { extraRoutes } = yield select(selectAuthData)
    const deleteUrl = extraRoutes?.devTools?.additionalSalesProductsDelete
    if (!deleteUrl) throw new Error('Missing delete url')
    const endpoint = deleteUrl?.replace('*id*', id)
    yield call(callApi, { endpoint, method: 'DELETE', showErrorSnackbar: false })
    const date = new Date().toISOString()
    yield put(deleteSuccess({ id, date }))
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: `Deleted ${name} (#${id})`,
      })
    }
  } catch (e) {
    yield put(deleteFailure())
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: `Error deleting ${name}`,
      })
    }
  }
}

export function* updateResourcesSaga({ productType, resources, enqueue, callback }) {
  try {
    yield put(setIsSavingResources(true))
    const { extraRoutes } = yield select(selectAuthData)
    const endpoint = extraRoutes?.additionalProductsUpdateResourcesUrl
    if (!endpoint) throw new Error('Missing update resources url')
    yield call(callApi, {
      endpoint,
      method: 'POST',
      payload: { resources, productType },
      showErrorSnackbar: false,
    })
    yield put(setIsSavingResources(false))
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.SUCCESS,
      message: `Resources updated for ${productType}`,
    })
    if (callback) callback()
  } catch (e) {
    yield put(setIsSavingResources(false))
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: e?.responseData?.error?.message ?? `Error updating resources for ${productType}`,
    })
    yield put({ type: additionalProductsToolsActions.FETCH_LIST, enqueue })
  }
}

function* restoreSaga({ id, name, enqueue }) {
  try {
    yield put(restoreRequest(id))
    const { extraRoutes } = yield select(selectAuthData)
    const restoreUrl = extraRoutes?.devTools?.additionalSalesProductsRestore
    if (!restoreUrl) throw new Error('Missing restore url')
    const endpoint = restoreUrl?.replace('*id*', id)
    yield call(callApi, { endpoint, method: 'PUT', showErrorSnackbar: false })
    yield put(restoreSuccess(id))
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: `Restored ${name} (#${id})`,
      })
    }
  } catch (e) {
    yield put(restoreFailure())
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: `Error restoring ${name}`,
      })
    }
  }
}

export default function* additionalProductsToolsSagas() {
  yield takeLatest(additionalProductsToolsActions.FETCH_LIST, fetchListSaga)
  yield takeLatest(additionalProductsToolsActions.DELETE, deleteSaga)
  yield takeLatest(additionalProductsToolsActions.RESTORE, restoreSaga)
  yield takeLatest(additionalProductsToolsActions.UPDATE_RESOURCES, updateResourcesSaga)
}
